import { useEffect } from "react";
import logo from "../../../../assets/NWLogo.png";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { GetIFRSQuestions } from "../../../../api/Quiz/GetIFRSQuestions";
import { StartIFRSExam } from "../../../../api/Quiz/StartIFRSExam";
import { toast } from "react-toastify";

const IFRSInstructionsSkip = () => {
  const { exam_id } = useParams();
  const nav = useNavigate();
  const location = useLocation();

  // Save the referring lecture URL once on mount so ResultsPage can navigate back
  useEffect(() => {
    const from = (location.state as { from?: string })?.from;
    if (from && exam_id) {
      sessionStorage.setItem(`ifrs-back-url-${exam_id}`, from);
    }
  }, [exam_id]);
  const StartExam = async () => {
    if (!exam_id) {
      toast.error("Exam ID is missing");
      return;
    }

    try {
      // First, call the start-exam API
      const startExamResponse = await StartIFRSExam(exam_id);

      if (startExamResponse.error) {
        console.error("Error starting exam:", startExamResponse.error);
        toast.error(startExamResponse.error.message || "Failed to start exam");
        // COMMENTED OUT: Modal state
        // setIsExamStarted(false);
        return;
      }

      // Get the student exam ID from the response (the 'id' field is the student exam ID)
      const studentExamId = startExamResponse.data?.data?.id || exam_id;
      // Get the original IFRS exam ID from the response
      const originalIfrsId = startExamResponse.data?.data?.ifrsId || exam_id;

      // Save student exam ID to localStorage for use in answer submissions
      // Use the original IFRS exam ID as part of the key
      if (studentExamId && originalIfrsId) {
        localStorage.setItem(
          `ifrs-student-exam-id-${originalIfrsId}`,
          studentExamId
        );
        // Also save the mapping from student exam ID to original IFRS exam ID
        localStorage.setItem(
          `ifrs-original-id-${studentExamId}`,
          originalIfrsId
        );
      }

      // Then fetch questions using the original IFRS exam ID
      const questionsResponse: any = await GetIFRSQuestions(originalIfrsId);
      console.log(questionsResponse);

      if (questionsResponse.data && questionsResponse.data.length > 0) {
        const firstQuestionId = questionsResponse.data[0].id;
        // Navigate using the original IFRS exam ID (exam_id from URL params)
        // The student exam ID is stored in localStorage and will be used for submissions
        nav(`/ifrs-exam/${originalIfrsId}/exam/${firstQuestionId}`, { replace: true });
      } else {
        toast.error("No questions found for this exam.");
        nav(-1);
      }
    } catch (error) {
      console.error("Unexpected error starting exam:", error);
      toast.error("An unexpected error occurred while starting the exam");
      // COMMENTED OUT: Modal state
      // setIsExamStarted(false);
    }
  };

  // ============================================================
  // NEW: Auto-start exam on component mount
  // This replaces the instructions flow
  // ============================================================
  useEffect(() => {
    StartExam();
  }, [exam_id]);

  // Show loading state while starting exam
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="mb-4">
          <img src={logo} alt="Logo" className="w-40 mx-auto" />
        </div>
        <p className="text-xl font-semibold">Starting your exam...</p>
        <p className="mt-2 text-gray-600">Please wait while we prepare your exam.</p>
      </div>
    </div>
  );

  // ============================================================
  // COMMENTED OUT: Original return with instructions UI
  // To re-enable instructions, uncomment the code below and comment out the loading state above
  // ============================================================
  // return (
  //   <>
  //     <div>{PAGES[currentPage]}</div>

  //     <footer className="exam-footer">
  //       {currentPage > 0 && (
  //         <button
  //           className="exam-footer-button"
  //           onClick={handlePrevious}
  //           disabled={isExamStarted}
  //         >
  //           <ArrowLeft size={18} />
  //           Previous
  //         </button>
  //       )}

  //       {currentPage < PAGES.length - 1 && (
  //         <button
  //           className="exam-footer-button"
  //           onClick={handleNext}
  //           disabled={isExamStarted}
  //         >
  //           Next
  //           <ArrowRight size={18} />
  //         </button>
  //       )}

  //       <button
  //         className="exam-footer-button"
  //         onClick={handleStartExam}
  //         disabled={isExamStarted}
  //       >
  //         {currentPage < PAGES.length - 1 ? "Skip" : "Start Exam"}
  //         <ArrowRightToLine size={18} />
  //       </button>
  //     </footer>

  //     <Modal isOpen={isExamStarted} header="Ready to begin?">
  //       <div className="start-modal">
  //         <div>
  //           <Svgify IconName="question" FontWeight="fill" />
  //         </div>
  //         <div>
  //           <p>If you are ready to begin your exam, please click 'Yes'.</p>
  //           <p>If you are not ready to begin your exam, please click 'No'.</p>
  //         </div>
  //       </div>
  //       <div className="start-modal-buttons">
  //         <button onClick={StartExam} disabled={isStartingExam}>
  //           {isStartingExam ? "Starting..." : "Yes"}
  //         </button>
  //         <button
  //           onClick={() => setIsExamStarted(false)}
  //           disabled={isStartingExam}
  //         >
  //           No
  //         </button>
  //       </div>
  //     </Modal>
  //   </>
  // );
};

// ============================================================
// COMMENTED OUT: Instruction page components
// These are preserved in case you want to re-enable instructions
// ============================================================

// const Introduction = () => {
//   return (
//     <article className="p-5">
//       <div className="flex items-center gap-2">
//         <img src={logo} alt="Logo" className="w-40 square" />
//         <p className="text-2xl">Nilewise</p>
//       </div>

//       <h4 className="text-xl mt-5">Introduction</h4>
//       <p>
//         This sample exam indicates how the live exam will be structured and
//         assessed, the likely layout and style of questions and the range of
//         response options that could be presented to you in each question's
//         workspace.
//       </p>
//       <p>
//         You should use this sample exam to become familiar with the workspace
//         and the features and functionality contained within the live exam.
//       </p>
//       <p>
//         This sample exam is reflective of the live exam experience but has some
//         differences:
//       </p>
//       <ul>
//         <li>
//           The live exam will be timed, however there is no time limit in this
//           exam.
//         </li>
//         <li>
//           In the live exam your answers entered into the workspace response
//           options will be expert-marked. In this exam you should use the
//           relevant solution material, available from the Past Exam section of
//           the ACCA website, to assess your performance.
//         </li>
//         <li>
//           If you want to sit this exam in exam-style conditions you should
//           answer the questions presented within a 3 hour 15 minute time period
//           without reviewing the solution material.
//         </li>
//       </ul>
//     </article>
//   );
// };

// const Instructions1 = ({
//   pageNumber,
//   total,
// }: {
//   pageNumber: number;
//   total: number;
// }) => {
//   return (
//     <article className="p-5">
//       <h4 className="text-xl mt-5">
//         Instructions ({pageNumber} of {total})
//       </h4>
//       <p>
//         The instructions displayed below are representative of those displayed
//         in the live exam. Where there are differences between this exam and the
//         live exam these are explained.
//       </p>

//       <h5 className="text-lg mt-5">General Instructions</h5>
//       <ul>
//         <li>
//           In this exam, the instruction screens are not timed however in the
//           live exam they will be available for a maximum of 10 minutes prior to
//           the exam starting.
//         </li>
//         <li>
//           In the live exam, the stated exam time will automatically start once
//           the 10 minute period has passed (or earlier if you choose to start the
//           exam within the 10 minute period).
//         </li>
//         <li>
//           A copy of the instruction screens can be accessed at any time during
//           the exam by selecting the Help button provided.
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">The Workspace</h5>
//       <ul>
//         <li>
//           <span>
//             Your exam consists of a number of questions. Each question is
//             presented in a workspace. Each workspace will include:
//           </span>
//           <ul>
//             <li>Introductory information for the question.</li>
//             <li>
//               Exhibits – these contain the question scenario content broken down
//               into sections or sources of information you will need to answer
//               the question requirement(s).
//             </li>
//             <li>
//               Requirements – these list the requirement(s) you are expected to
//               complete in the workspace.
//             </li>
//             <li>
//               Response Options – you will be presented with one or more word
//               processor and/or spreadsheet response options within which to
//               construct your answer.
//             </li>
//           </ul>
//         </li>
//         <li>
//           When selected, the exhibits, requirements and response options will
//           display in windows, which can be moved and resized as required.
//         </li>
//         <li>
//           You can close each window individually or close all windows at once by
//           selecting on the top toolbar. This minimises the open windows to the
//           left-hand side of the screen. Any answers you have entered into the
//           response options will be saved and you can re-open them and change
//           your answers at any time.
//         </li>
//         <li>
//           There is a splitter bar which can be used for you to see more or less
//           of the left or right hand side of the screen, as required.
//         </li>
//         <li>
//           You can highlight or strikethrough text in the introductory
//           information, PDF exhibits or requirements by selecting (WILL ADD
//           IMAGE) or (WILL ADD IMAGE) .
//         </li>
//         <li>
//           You can copy and paste between exhibits, requirements and response
//           options by using the Ctrl-C (Copy) and Ctrl-V (Paste) shortcuts.{" "}
//           <strong>Note:</strong>
//           When pasting into a spreadsheet in the live exam you must double-click
//           the cell or paste directly into the formula bar. In this exam you must
//           paste into the spreadsheet by selecting the cell.
//         </li>

//         <li>
//           You should use this sample exam to become familiar with the workspace
//           and the features and functionality contained within the live exam.
//         </li>
//         <li>
//           This sample exam is reflective of the live exam experience but has
//           some differences:
//         </li>
//       </ul>
//     </article>
//   );
// };

// const Instructions2 = ({
//   pageNumber,
//   total,
// }: {
//   pageNumber: number;
//   total: number;
// }) => {
//   return (
//     <article className="p-5">
//       <h4 className="text-xl mt-5">
//         Instructions ({pageNumber} of {total})
//       </h4>
//       <h5 className="text-lg mt-5">Navigation</h5>
//       <ul>
//         <li>
//           The question number you are viewing is displayed in the top display
//           bar. You can hide or restore this display by selecting .
//         </li>
//         <li>
//           You can navigate between screens by selecting or or by clicking on a
//           question number from the Navigator{" "}
//           <button className="exam-footer-button" disabled>
//             <ArrowLeft size={14} />
//             Previous
//           </button>{" "}
//           or{" "}
//           <button className="exam-footer-button" disabled>
//             Next
//             <ArrowRight size={14} />
//           </button>{" "}
//           Item Review screens.
//         </li>
//         <li>
//           You can revisit questions and change your answers at any time during
//           the exam.
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">Flag for Review</h5>
//       <ul>
//         <li>
//           If you wish to revisit/review a question later in the exam, click
//           (WILL ADD IMAGE).
//         </li>
//         <li>Click the button again to remove the flag.</li>
//       </ul>

//       <h5 className="text-lg mt-5">Help</h5>
//       <ul>
//         <li>
//           <span>
//             Click the <strong>Help</strong> button provided to access:
//           </span>
//           <ul>
//             <li>A copy of these exam instructions.</li>
//             <li>Help and guidance on workspaces.</li>
//             <li>
//               Formulae sheets/tax tables if your exam requires these. Note that
//               the name of the Help button will indicate if formulae sheets/tax
//               tables are available i.e. (WILL ADD IMAGE), (WILL ADD IMAGE) or
//               (WILL ADD IMAGE).
//             </li>
//           </ul>
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">Calculator</h5>
//       <ul>
//         <li>
//           <span>
//             Click the <strong>Help</strong> button provided to access:
//           </span>
//           <ul>
//             <li>
//               You have the option to use the on-screen standard or scientific
//               calculators by selecting (WILL ADD IMAGE).
//             </li>
//             <li>
//               Note that in the live exam you are also permitted to use your own
//               calculator providing it does not have the facility to store or
//               display text.
//             </li>
//           </ul>
//         </li>
//       </ul>
//     </article>
//   );
// };

// const Instructions3 = ({
//   pageNumber,
//   total,
// }: {
//   pageNumber: number;
//   total: number;
// }) => {
//   return (
//     <article className="p-5">
//       <h4 className="text-xl mt-5">
//         Instructions ({pageNumber} of {total})
//       </h4>
//       <h5 className="text-lg mt-5">Workings/Scratch Pad</h5>
//       <ul>
//         <li>
//           You may use an on-screen Scratch Pad to make notes/workings by
//           selecting (WILL ADD IMAGE)
//         </li>
//         <li>
//           You can cut and copy text from the Scratch Pad and paste into your
//           response options either by using the Ctrl-C (Copy), Ctrl-X (Cut) and
//           Ctrl-V (Paste) shortcuts, or the on-screen buttons
//         </li>
//         <li>
//           The Scratch Pad retains all notes/workings entered for all workspaces
//           and these are available for the duration of the exam. They will not be
//           submitted for marking after your live exam.
//         </li>
//         <li>
//           You will also be provided with paper for notes/workings for your live
//           exam, should you prefer to use it. This will not be submitted for
//           marking. It will be collected at the end of the exam and must not be
//           removed from the exam room.
//         </li>
//         <li>
//           <strong>Important:</strong>
//           <ul>
//             <li>
//               The notes/workings entered onto the Scratch Pad or your workings
//               paper during the live exam will not be marked
//             </li>
//             <li>
//               If you want the marker to see any notes/workings for question
//               requirements in the live exam you must show them within the
//               relevant response options_
//             </li>
//           </ul>
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">Symbol</h5>
//       <ul>
//         <li>
//           You can add a selection of currency symbols to your answers by
//           selecting (WILL ADD IMAGE) on the top toolbar.
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">Navigator Screen</h5>
//       <ul>
//         <li>
//           Navigator can be accessed at any time during the exam by selecting
//           (WILL ADD IMAGE).
//         </li>
//         <li>
//           This screen allows you to jump to any question number in the exam.
//         </li>
//         <li>
//           This screen allows you to jump to any question number in the exam.
//         </li>
//       </ul>
//     </article>
//   );
// };

// const Instructions4 = ({
//   pageNumber,
//   total,
// }: {
//   pageNumber: number;
//   total: number;
// }) => {
//   return (
//     <article className="p-5">
//       <h4 className="text-xl mt-5">
//         Instructions ({pageNumber} of {total})
//       </h4>
//       <h5 className="text-lg mt-5">Reviewing the Exam</h5>
//       <ul>
//         <li>
//           You can review this exam once you have attempted any, or all, of the
//           questions.
//         </li>
//         <li>
//           To do this:
//           <ul>
//             <li>
//               Navigate to the <strong>last question</strong> in the exam
//             </li>
//             <li>
//               Click{" "}
//               <button className="exam-footer-button" disabled>
//                 Next
//                 <ArrowRight size={14} />
//               </button>
//             </li>
//           </ul>
//         </li>
//         <li>This takes you to the Item Review screen</li>
//       </ul>

//       <h5 className="text-lg mt-5">Item Review Screen</h5>
//       <ul>
//         <li>
//           his screen gives you an opportunity to see the flag and attempted
//           status of all questions and to review your answers before you finally
//           exit the exam.
//         </li>
//         <li>
//           You can select individual questions you wish to revisit, or quickly
//           access groups of questions from the Item Review screen.
//         </li>
//         <li>
//           During the item review period Navigator is not available however you
//           can navigate to questions by selecting{" "}
//           <button className="exam-footer-button" disabled>
//             Next
//             <ArrowRight size={14} />
//           </button>
//           ,{" "}
//           <button className="exam-footer-button" disabled>
//             Previous
//             <ArrowLeft size={14} />
//           </button>{" "}
//           or{" "}
//           <button className="exam-footer-button" disabled>
//             <ArrowRightToLine size={14} />
//             Review Screen
//           </button>
//         </li>
//         <li>
//           When reviewing questions you can change your answer and click
//           <button className="exam-footer-button" disabled>
//             <ArrowRightToLine size={14} />
//             Review Screen
//           </button>{" "}
//           to view any updated status on the Item Review screen.
//         </li>
//         <li>
//           You can review your answers against the relevant solution material
//           provided in the Past Exam section of the ACCA website.
//         </li>
//       </ul>

//       <h5 className="text-lg mt-5">Ending the Exam</h5>
//       <ul>
//         <li>
//           Once you have completed your item review and wish to finally end the
//           exam click
//           <button className="exam-footer-button" disabled>
//             <LogOut size={14} />
//             End Exam
//           </button>{" "}
//           from the Item Review screen.
//         </li>
//         <li>Once you end the exam, you cannot revisit any questions.</li>
//       </ul>

//       <p className="mt-10">
//         Select <strong>Next</strong> to move to the Exam Summary screen.
//       </p>
//     </article>
//   );
// };

// const Summary = () => {
//   return (
//     <article className="p-5">
//       <h4 className="text-lg mt-5 mb-5">Exam Summary</h4>

//       <p style={{ marginBottom: "2rem" }}>
//         <strong>Time allowed:</strong> This sample exam is not timed.
//       </p>

//       <p>
//         This exam contains <strong>4 questions</strong> worth{" "}
//         <strong>25 marks</strong> each.
//       </p>

//       <p style={{ marginBottom: "2rem" }}>
//         <strong>100 marks</strong> in total.
//       </p>

//       <p style={{ marginBottom: "2rem" }}>All questions are compulsory.</p>

//       <p className="font-bold">Important:</p>
//       <p>In your live exam, you should:</p>

//       <ul className="ms-10 mb-5">
//         <li>
//           Enter your answer for each question in the response options provided
//           for that question. Any answers entered into a response option provided
//           for a different question will not be marked.
//         </li>
//         <li>
//           Indicate which requirement each of your responses relate to so that
//           this is clear for markers.
//         </li>
//         <li>
//           Show all notes/workings that you want the marker to see within your
//           responses. Remember, any notes/workings made on the Scratch Pad or on
//           your workings paper will not be marked.
//         </li>
//       </ul>

//       <p style={{ marginBottom: "2rem" }}>
//         Click <strong>Next</strong> to start your exam.
//       </p>

//       <hr style={{ marginBottom: "2rem" }} />

//       <p className="text-gray-900" style={{ fontWeight: 300 }}>
//         This exam makes reference to trade marks which are owned by the IFRS
//         Foundation, and used by ACCA under licence. The IFRS Foundation has
//         trade marks registered around the world including: 'IAS®', 'IASB®',
//         ISSB™, 'IFRIC®', 'IFRS®', the IFRS® logo, 'IFRS for SMEs®', IFRS for
//         SMEs® logo, the 'Hexagon Device', 'International Accounting Standards®',
//         'International Financial Reporting Standards®', 'NIIF®' and 'SIC®'.
//         Further details of the IFRS Foundation's trade marks are available from
//         the IFRS Foundation on request.
//       </p>
//     </article>
//   );
// };

export default IFRSInstructionsSkip;